<template>
  <div>
    <CContainer fluid class="grid-stripped">
      <PFRow :label="$pgettext('role.label', 'Role Name')">
          <CInput v-model="form.name" :placeholder="$pgettext('role.placeholder','Name')"/>
        </PFRow>
        <PFRow :label="$pgettext('role.label', 'Permissions')">
          <div class="permissions-container">
            <div class="permissions-search">
              <CInput 
                v-model="searchFilter"
                :placeholder="$pgettext('role.search', 'Cerca permessi...')"
                class="permission-search-input">
                <template #prepend>
                  <CIcon name="cilSearch" class="search-icon"/>
                </template>
                <template #append v-if="searchFilter">
                  <CButton 
                    @click="searchFilter = ''" 
                    variant="ghost" 
                    size="sm"
                    class="clear-search-btn">
                    <CIcon name="cilX" size="sm"/>
                  </CButton>
                </template>
              </CInput>
            </div>
            <Permission 
              v-if="permissions && dataReady"
              :active="activePermissions" 
              :permissions="permissions" 
              :search-filter="searchFilter"
              @click="onClickPermission"/>
          </div>
        </PFRow>
        <PFRow :label="$pgettext('role.label', 'Visible By')">
          <VisibleBySelector
            v-if="dataReady"
            :value="form.visible_by"
            :available-roles="availableRoles"
            :context-id="contextId"
            @input="updateVisibleBy"
          />
        </PFRow>
        <PFRow 
            :label="$pgettext('role.label', 'Highlighted')"
            :help-text="$pgettext('role.help', 'Highlighted roles are shown first when assigning roles')">
          <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                   :checked.sync="form.is_highlighted"/>
        </PFRow>
        <PFRow :label="$pgettext('role.label', 'Description')">
          <CTextarea v-model="form.description" :placeholder="$pgettext('role.placeholder','Description')"/>
        </PFRow>
    </CContainer>
  </div>

</template>

<script>
import {mapActions, mapGetters} from "vuex";
import PFRow from "@/domain/core/components/PFRow.vue";
import Permission from "@/domain/roles/components/Permission.vue";
import VisibleBySelector from "@/domain/roles/components/VisibleBySelector.vue";

export default {
  name: "Form",
  components: {Permission, PFRow, VisibleBySelector},
  props: {
    data: {
      type: Object,
      required: true
    },
    save_text: {
      type: String,
      required: true
    },
    edit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        name: '',
        permissions: {},
        visible_by: [],
        description: '',
        is_highlighted: false,
      },
      dot_permissions: {},
      searchFilter: '',
      dataReady: false,
      contextType: null,
      contextId: null,
      availableRoles: [],
      preservedHiddenRoles: []
    }
  },
  async mounted() {
    await this.fetchPermissions()
    this.detectContext()
    await this.loadVisibleRoles()
    this.onReset()
    this.dataReady = true
  },
  emits: ['save'],
  computed: {
    ...mapGetters('roles', [
      'permissions'
    ]),
    activePermissions() {
      const result = this.fromDotPermission(this.dot_permissions)
      // Assicuriamoci di restituire sempre un oggetto, anche se vuoto
      return result || {}
    }
  },
  methods: {
    ...mapActions('roles', [
      'fetchPermissions',
      'fetchVisibleRolesByContext'
    ]),
    detectContext() {
      const path = this.$route.path
      const params = this.$route.params
      
      if (path.includes('/contract_manager/')) {
        this.contextType = 'contract_manager'
        // Prende l'ID dal percorso, potrebbe essere 'id' o 'contract_manager'
        this.contextId = params.id || params.contract_manager
      } else {
        // Contesto globale per admin
        this.contextType = 'global'
        this.contextId = null
      }
    },
    async loadVisibleRoles() {
      if (this.contextType && this.contextType !== 'global') {
        const response = await this.fetchVisibleRolesByContext({
          contextType: this.contextType,
          contextId: this.contextId
        })
        this.availableRoles = response.results || response
      } else {
        const response = await this.fetchVisibleRolesByContext({
          contextType: 'global',
          contextId: null
        })
        this.availableRoles = response.results || response
      }
    },
    onReset() {
      this.form = {...this.data}
      this.dot_permissions = this.toDotPermission(this.form.permissions)
      
      if (this.edit && this.form.visible_by) {
        // Usa solo gli ID numerici
        const availableIds = this.availableRoles.map(r => r.id)
        this.preservedHiddenRoles = this.form.visible_by.filter(
          id => !availableIds.includes(id)
        )
      }
    },
    onClickPermission({key, value}) {
      if (value === '*') {
        if (this.dot_permissions[key]) {
          this.$delete(this.dot_permissions, key)
          return
        } else {
          this.$set(this.dot_permissions, key, '*')
          this.removeSubKeys(key)
          return
        }
      }
      if (this.dot_permissions[key])
        this.$delete(this.dot_permissions, key)
      else
        this.$set(this.dot_permissions, key, true)
    },
    removeSubKeys(key) {
      Object.keys(this.dot_permissions).forEach(k => {
        if (k === key) return
        if (k.indexOf(key) === 0)
          this.$delete(this.dot_permissions, k)
      })
    },
    fromDotPermission(dot_permissions) {
      return Object.keys(dot_permissions).reduce((acc, key) => {
        const keys = key.split('.');
        let obj = acc
        for (let i = 0; i < keys.length; i++) {
          const k = keys[i];
          if (i === keys.length - 1)
            obj[k] = dot_permissions[key]
          else {
            if (!obj[k])
              obj[k] = {}
            obj = obj[k]
          }
        }
        return acc
      }, {})
    },
    toDotPermission(obj, prefix = '') {
      return Object.keys(obj).reduce((acc, key) => {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          Object.assign(acc, this.toDotPermission(obj[key], newKey));
        } else {
          acc[newKey] = obj[key];
        }
        return acc;
      }, {});
    },
    updateVisibleBy(selectedRoles) {
      const combined = [...new Set([...selectedRoles, ...this.preservedHiddenRoles])]
      this.form.visible_by = combined
    },
    async onSave() {
      this.form.permissions = this.activePermissions
      this.$emit('save', this.form)
    },
  }
}
</script>

<style scoped>
.permissions-container {
  width: 100%;
}

.permissions-search {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.permission-search-input {
  max-width: 300px;
  min-width: 250px;
}

.search-icon {
  color: #64748b;
  padding: 0 0.5rem;
}

.clear-search-btn {
  padding: 0.25rem;
  border: none;
  background: transparent;
}

.clear-search-btn:hover {
  background: #f1f5f9;
  border-radius: 0.25rem;
}

/* Responsive per mobile */
@media (max-width: 640px) {
  .permissions-search {
    justify-content: stretch;
  }
  
  .permission-search-input {
    max-width: 100%;
    min-width: 100%;
  }
}
</style>